import React, { useMemo, useState } from "react";
import JuanEstebanImg from "../candidates/JuanEsteban.jpg";
import LihueImg from "../candidates/Lihue.png";
import DanielImg from "../candidates/daniel.jpeg";
import JuanFelipeImg from "../candidates/juan.png";

const PROFILE_LINKS = {
  "Juan Esteban": "https://jobs.aiarchitech.com/client/candidates/juanesteban",
  "Lihué": "https://jobs.aiarchitech.com/client/candidates/linhue",
  "Daniel": "https://jobs.aiarchitech.com/client/candidates/Daniel",
  "Juan Felipe": "https://jobs.aiarchitech.com/client/candidates/juanfelipe",
};

const HEADSHOTS = {
  "Juan Esteban": JuanEstebanImg,
  "Lihué": LihueImg,
  "Daniel": DanielImg,
  "Juan Felipe": JuanFelipeImg,
};

const rankedOverview = [
  {
    rank: 1,
    name: "Juan Esteban",
    title: "Most complete end-to-end operator",
    summary:
      "The most well-rounded profile in the pool: combines narrative-driven lead generation thinking, sustained content engine operations, SEO-aware topic research, solid video production, and bilingual communication into a single hire. Needs a short ramp on commercial local services context, but brings the strongest combination of strategy and execution ownership.",
  },
  {
    rank: 2,
    name: "Lihué",
    title: "Strongest lead-gen and content strategy operator",
    summary:
      "Brings the most developed lead generation mechanics in the pool — ManyChat automations, funnel design, email sequences, UTM tracking — wired directly into her content creation process. Strongest platform operations breadth and SEO copywriting instincts. Video production is CapCut/Canva-level; pairs best with a production-focused counterpart or AI Architect who supplies visual assets.",
  },
  {
    rank: 3,
    name: "Daniel",
    title: "Best AI-augmented video production craft",
    summary:
      "Deepest AI video toolchain in the pool (Runway, Kling, Midjourney, Suno, ElevenLabs) paired with 20 years of post-production craft. Strongest short-form video output quality and hook instincts. Needs topic research and content strategy direction from the AI Architect; works best as a production executor with clear creative briefs.",
  },
  {
    rank: 4,
    name: "Juan Felipe",
    title: "Highest production quality ceiling",
    summary:
      "Technically elite filmmaker and post-production specialist with agency-level color grading, motion graphics, and compositing skills that no other candidate in this pool can match. The right hire if premium visual quality is the immediate top priority. Needs a strong strategist counterpart to own content direction, copywriting, lead-gen mechanics, and publishing operations.",
  },
];

const dimensions = [
  { id: 1, label: "AI & Content Tools" },
  { id: 2, label: "Core Platforms & Publishing Ops" },
  { id: 3, label: "Performance & Lead Gen Mindset" },
  { id: 4, label: "Short-Form Video Production & Editing" },
  { id: 5, label: "Storytelling, Hooks & Audience Psychology" },
  { id: 6, label: "Search Intent & Topic Research" },
  { id: 7, label: "Systemic Thinking & Content Engine Design" },
  { id: 8, label: "Communication, Ownership & Collaboration" },
  { id: 9, label: "Time Zone & Overlap Fit" },
];

const scores = {
  "Juan Esteban": [7, 8, 8, 7, 9, 7, 8, 9, 8],
  "Lihué":        [7, 9, 9, 6, 8, 7, 8, 9, 7],
  "Daniel":       [9, 8, 7, 9, 8, 4, 7, 8, 8],
  "Juan Felipe":  [5, 5, 5, 10, 7, 4, 5, 6, 8],
};

const comparisons = [
  {
    sectionTitle: "Juan Esteban Comparison",
    items: [
      {
        heading: "Compared to Lihué",
        body: "Juan Esteban and Lihué are the two strongest strategic operators in the pool. Juan Esteban edges ahead on storytelling depth, narrative frameworks, and content-to-conversion systems thinking. Lihué edges ahead on platform operations breadth, ManyChat/funnel automation mechanics, and SEO-integrated copywriting. If you need one hire who can own both strategy and production, Juan Esteban is the pick. If the role is primarily content distribution, funnel mechanics, and lead capture operations, Lihué is stronger.",
      },
      {
        heading: "Compared to Daniel",
        body: "Juan Esteban brings stronger strategy, topic research, lead-gen thinking, and communication ownership. Daniel brings significantly stronger AI video toolchain depth and post-production craft. A Juan Esteban + Daniel pairing would cover the full stack: Juan Esteban owns the content direction and narrative layer; Daniel owns the visual production and AI generation layer.",
      },
      {
        heading: "Compared to Juan Felipe",
        body: "Juan Esteban is the stronger fit for the Content Producer role as scoped today: he brings strategy, copywriting, lead-gen mechanics, platform operations, and solid video production in one profile. Juan Felipe is the stronger pure production hire if visual quality is the top priority. Juan Felipe needs a strategist to direct him; Juan Esteban can direct himself.",
      },
    ],
  },
  {
    sectionTitle: "Lihué Comparison",
    items: [
      {
        heading: "Compared to Juan Esteban",
        body: "Lihué is stronger on platform operations, funnel automation, and lead capture mechanics — she can wire ManyChat to Zapier to CRM and build the capture layer into every piece of content by default. Juan Esteban is stronger on narrative structure, storytelling, video production, and end-to-end content systems. If the immediate priority is lead capture operations and funnel mechanics, Lihué leads. If the priority is content quality and strategic ownership, Juan Esteban leads.",
      },
      {
        heading: "Compared to Daniel",
        body: "Lihué is the stronger strategist and lead-gen operator; Daniel is the stronger video producer and AI content creator. Lihué will get the funnel architecture right and ensure content is connected to lead outcomes. Daniel will make the content look and sound better. As a two-person content team, they would complement each other almost perfectly.",
      },
      {
        heading: "Compared to Juan Felipe",
        body: "Lihué covers almost everything Juan Felipe does not: strategy, copywriting, lead-gen mechanics, SEO, platform operations, and content scheduling. Juan Felipe covers almost everything Lihué does not: cinematic production quality, color grading, motion graphics, and post-production finishing. As a standalone Content Producer, Lihué is the stronger fit for the role as scoped.",
      },
    ],
  },
  {
    sectionTitle: "Daniel Comparison",
    items: [
      {
        heading: "Compared to Juan Esteban",
        body: "Daniel is the stronger pure video producer and AI content creator; Juan Esteban is the stronger strategist, storyteller, and content systems operator. Daniel needs topic direction and strategic briefs to work from; Juan Esteban generates his own direction. If the AI Architect is in place to supply strategy, Daniel's production output will be higher quality. If the Content Producer needs to be self-directed from day one, Juan Esteban is the safer pick.",
      },
      {
        heading: "Compared to Lihué",
        body: "Daniel is significantly stronger on video production craft and AI generation toolchain. Lihué is significantly stronger on funnel mechanics, lead capture automation, SEO copywriting, and platform operations. Daniel makes better content; Lihué makes content that works harder as a lead generation system. The ideal scenario is both on the same team.",
      },
      {
        heading: "Compared to Juan Felipe",
        body: "Both are production-first candidates, but Daniel is significantly more suited to the Content Producer role: he has marketing director experience, understands campaign content and CTAs, and brings an advanced AI video toolchain. Juan Felipe is the stronger pure post-production technician. For this specific role, Daniel is the stronger choice between the two production-focused candidates.",
      },
    ],
  },
  {
    sectionTitle: "Juan Felipe Comparison",
    items: [
      {
        heading: "Compared to Juan Esteban",
        body: "Juan Felipe's post-production quality ceiling is higher than any other candidate in this pool. But Juan Esteban covers everything Juan Felipe lacks: strategy, lead-gen thinking, SEO topic research, platform operations, copywriting, and content systems design. Juan Felipe is the right hire when premium visual production is the single top priority and a strong strategist is already in place to direct him.",
      },
      {
        heading: "Compared to Lihué",
        body: "Juan Felipe and Lihué have almost no overlap: he is entirely production-focused, she is entirely strategy-and-operations-focused. As a standalone Content Producer hire, Lihué covers far more of the role's requirements. As part of a two-person content team, Juan Felipe's production quality paired with Lihué's strategy and funnel mechanics would be a powerful combination.",
      },
      {
        heading: "Compared to Daniel",
        body: "Juan Felipe brings higher technical post-production craft: superior color grading, motion graphics, and cinematic finishing. Daniel brings a stronger overall fit for the Content Producer role: marketing experience, AI video toolchain, campaign content instincts, and hook-first short-form thinking. For this role as scoped today, Daniel is the more immediately deployable production hire.",
      },
    ],
  },
];

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function tone(score) {
  if (score >= 7) return "good";
  if (score >= 5) return "mid";
  return "warn";
}

function safeHref(name) {
  return PROFILE_LINKS[name] || "#";
}

function safeHeadshot(name) {
  return HEADSHOTS[name] || "";
}

function NewTabLink({ href, className, children }) {
  return (
    <a className={className} href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  );
}

function SectionShell({ kicker, title, subtitle, right, children }) {
  return (
    <section className="block">
      <div className="blockTop">
        <div className="blockTopLeft">
          {kicker && <div className="blockKicker">{kicker}</div>}
          <div className="blockTitleRow">
            <h2 className="blockTitle">{title}</h2>
            {right ? <div className="blockRight">{right}</div> : null}
          </div>
          {subtitle ? <div className="blockSubtitle">{subtitle}</div> : null}
        </div>
      </div>
      <div className="blockBody">{children}</div>
    </section>
  );
}

export default function CandidateFitDashboardContentProducerManny() {
  const [openSections, setOpenSections] = useState({
    "Juan Esteban Comparison": true,
  });

  const candidateKeys = useMemo(
    () => ["Juan Esteban", "Lihué", "Daniel", "Juan Felipe"],
    []
  );

  const toggleSection = (title) =>
    setOpenSections((p) => ({ ...p, [title]: !p[title] }));

  return (
    <div className="page">
      <div className="bgGlow" aria-hidden="true" />

      <div className="container">
        <header className="hero">
          <div className="heroKicker">Handy Manny's Remodeling</div>
          <h1 className="heroTitle">Content Producer — Candidate Fit Dashboard</h1>
          <p className="heroSub">
            Ranked overview, dimension scoring, and interview-based fit notes.
          </p>
        </header>

        {/* SECTION 1 */}
        <SectionShell
          kicker="Section 01"
          title="Comparative Fit Summary"
          subtitle="A high-level snapshot of each candidate, ranked from strongest overall fit to least immediate fit."
        >
          <div className="grid4">
            {rankedOverview.map((c) => (
              <article
                key={c.name}
                className={`card rankCard ${c.rank === 1 ? "cardHero" : ""}`}
              >
                <div className="rankRow">
                  <div className="rankBadge">#{c.rank}</div>
                  <div className="rankName">{c.name}</div>
                  <div className="rankSpacer" />
                </div>

                <div className="rankAvatarWrap">
                  <img
                    className="rankAvatarBig"
                    src={safeHeadshot(c.name)}
                    alt={`${c.name} headshot`}
                    loading="lazy"
                  />
                </div>

                <div className="rankTitle">{c.title}</div>
                <p className="rankBody">{c.summary}</p>

                <div className="cardFooter">
                  <NewTabLink className="selectBtn" href={safeHref(c.name)}>
                    Open profile →
                  </NewTabLink>
                </div>
              </article>
            ))}
          </div>
        </SectionShell>

        {/* SECTION 2 */}
        <SectionShell
          kicker="Section 02"
          title="Score Comparison"
          subtitle="Scores per dimension (1–10)."
        >
          <div className="card tableCard">
            <div className="tableWrap" role="region" aria-label="Score table">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th dimCol">Dimension</th>
                    {candidateKeys.map((k) => (
                      <th key={k} className="th candCol">
                        <div className="thCand">
                          <div className="thName">{k}</div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {dimensions.map((d, idx) => (
                    <tr key={d.id} className="tr">
                      <td className="td dimCell">
                        <div className="dimLabel">
                          {d.id}. {d.label}
                        </div>
                      </td>

                      {candidateKeys.map((k) => {
                        const s = scores[k][idx];
                        const pct = clamp((s / 10) * 100, 0, 100);
                        const t = tone(s);

                        return (
                          <td key={`${d.id}-${k}`} className="td scoreCell">
                            <div className="scoreBox">
                              <div className="scoreTop">
                                <div className="scoreNum">{s}</div>
                                <div className={`scoreDot ${t}`} aria-hidden="true" />
                              </div>
                              <div className="bar">
                                <div
                                  className={`barFill ${t}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* Weighted Average Row */}
                  <tr className="tr avgRow">
                    <td className="td dimCell">
                      <div className="dimLabel avgLabel">Weighted Average</div>
                    </td>
                    {candidateKeys.map((k) => {
                      const avg =
                        scores[k].reduce((a, b) => a + b, 0) / scores[k].length;
                      const rounded = Math.round(avg * 10) / 10;
                      const t = tone(avg);
                      return (
                        <td key={`avg-${k}`} className="td scoreCell">
                          <div className="scoreBox">
                            <div className="scoreTop">
                              <div className="scoreNum avgNum">{rounded}</div>
                              <div className={`scoreDot ${t}`} aria-hidden="true" />
                            </div>
                            <div className="bar">
                              <div
                                className={`barFill ${t}`}
                                style={{ width: `${clamp((avg / 10) * 100, 0, 100)}%` }}
                              />
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </SectionShell>

        {/* SECTION 3 */}
        <SectionShell
          kicker="Section 03"
          title="Candidate Comparisons"
          subtitle="Interview-based notes comparing each candidate against the others."
          right={<span className="tinyNote">Expandable details</span>}
        >
          <div className="card compareShell">
            <div className="compareShellInner">
              {comparisons.map((sec, i) => {
                const isOpen = !!openSections[sec.sectionTitle];
                return (
                  <div
                    key={sec.sectionTitle}
                    className={`rowAcc ${i === 0 ? "first" : ""}`}
                  >
                    <button
                      type="button"
                      className="rowAccHead"
                      onClick={() => toggleSection(sec.sectionTitle)}
                      aria-expanded={isOpen}
                    >
                      <div className="rowAccLeft">
                        <div className="rowAccTitle">{sec.sectionTitle}</div>
                        <div className="rowAccHint">Click to expand</div>
                      </div>
                      <div className="rowAccIcon" aria-hidden="true">
                        {isOpen ? "–" : "+"}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="rowAccBody">
                        {sec.items.map((it) => (
                          <div key={it.heading} className="compareRow">
                            <div className="compareHeading">{it.heading}</div>
                            <div className="compareText">{it.body}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </SectionShell>
      </div>

      <style>{`
        :root {
          --aa-navy: #01014C;
          --aa-navy2: #0A0A80;
          --aa-cyan: #36D1FF;
          --aa-cream: #FFFDFB;
          --aa-orange: #EB5D2A;
        }

        html, body { background: var(--aa-navy); }

        .page {
          min-height: 100vh;
          padding: 26px 18px 60px;
          position: relative;
          overflow: hidden;
          color: #eaf1ff;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
          font-size: 16px;
        }

        .bgGlow {
          position: absolute;
          inset: -220px;
          background:
            radial-gradient(900px 620px at 18% 18%, rgba(54,209,255,0.22), transparent 62%),
            radial-gradient(760px 520px at 78% 22%, rgba(10,10,128,0.22), transparent 58%),
            radial-gradient(980px 780px at 52% 92%, rgba(54,209,255,0.12), transparent 62%),
            radial-gradient(900px 700px at 50% 50%, rgba(255,253,251,0.04), transparent 65%);
          pointer-events: none;
          z-index: 0;
        }

        .container { max-width: 1200px; margin: 0 auto; position: relative; z-index: 1; }

        /* HERO */
        .hero { margin-bottom: 18px; }
        .heroKicker {
          font-size: 13px; letter-spacing: .14em; text-transform: uppercase;
          color: rgba(234,241,255,.78);
          margin-bottom: 10px;
        }
        .heroTitle {
          margin: 0;
          font-size: 36px;
          font-weight: 900;
          line-height: 1.06;
          background: linear-gradient(90deg, var(--aa-cream) 0%, rgba(54,209,255,.95) 55%, rgba(255,253,251,.85) 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .heroSub {
          margin: 12px 0 0;
          max-width: 72ch;
          color: rgba(234,241,255,.78);
          font-size: 15px;
          line-height: 1.65;
        }

        /* SECTION SHELLS */
        .block {
          margin-top: 14px;
          border-radius: 26px;
          border: 1px solid rgba(54,209,255,.22);
          background: rgba(255,255,255,.055);
          box-shadow: 0 22px 70px rgba(0,0,0,.34);
          backdrop-filter: blur(12px);
          overflow: hidden;
        }

        .blockTop {
          padding: 16px 16px 14px;
          background:
            radial-gradient(900px 200px at 10% 0%, rgba(54,209,255,.18), transparent 55%),
            radial-gradient(700px 220px at 90% 0%, rgba(10,10,128,.16), transparent 55%),
            rgba(0,0,0,.08);
          border-bottom: 1px solid rgba(54,209,255,.16);
        }

        .blockKicker {
          font-size: 13px;
          letter-spacing: .12em;
          text-transform: uppercase;
          color: rgba(234,241,255,.70);
          margin-bottom: 8px;
        }

        .blockTitleRow {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .blockTitle {
          margin: 0;
          font-size: 20px;
          font-weight: 950;
          letter-spacing: .01em;
          color: rgba(245,248,255,.96);
        }

        .blockSubtitle {
          margin-top: 10px;
          color: rgba(234,241,255,.76);
          font-size: 14px;
          line-height: 1.7;
          max-width: 95ch;
        }

        .blockBody { padding: 14px 14px 16px; }
        .tinyNote { font-size: 13px; color: rgba(234,241,255,.70); }

        /* CARDS */
        .card {
          border-radius: 22px;
          background: rgba(255,255,255,.065);
          border: 1px solid rgba(54,209,255,.18);
          box-shadow: 0 18px 60px rgba(0,0,0,.26);
          backdrop-filter: blur(12px);
        }

        .selectBtn {
          width: 100%;
          font-weight: 900;
          border-radius: 16px;
          background: var(--aa-cyan);
          color: var(--aa-navy);
          border: 1px solid rgba(54,209,255,.55);
          box-shadow: 0 0 22px rgba(54,209,255,.35);
          padding: 11px 14px;
          font-size: 14px;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform .12s ease, background .12s ease, box-shadow .12s ease;
        }
        .selectBtn:hover {
          background: #5FE2FF;
          box-shadow: 0 0 30px rgba(54,209,255,.55);
          transform: translateY(-1px);
        }

        /* SECTION 1 — 4-col grid */
        .grid4 {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
        }
        @media (max-width: 1100px) { .grid4 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 640px)  { .grid4 { grid-template-columns: 1fr; } }

        .rankCard {
          padding: 14px;
          display: flex;
          flex-direction: column;
          min-height: 292px;
          position: relative;
          overflow: hidden;
        }
        .cardHero { border-color: rgba(54,209,255,.42); }
        .cardHero:before {
          content: "";
          position: absolute;
          inset: 0;
          background: radial-gradient(520px 320px at 18% 0%, rgba(54,209,255,.18), transparent 58%);
          pointer-events: none;
        }

        .rankRow { display: flex; align-items: center; gap: 10px; position: relative; z-index: 1; }
        .rankBadge {
          width: 36px; height: 36px;
          display: grid; place-items: center;
          border-radius: 12px;
          background: rgba(0,0,0,.16);
          border: 1px solid rgba(54,209,255,.22);
          font-weight: 900;
          font-size: 14px;
        }
        .rankName { font-weight: 950; font-size: 16px; }
        .rankSpacer { flex: 1; }

        .rankAvatarWrap {
          display: grid;
          place-items: center;
          margin-top: 12px;
          margin-bottom: 6px;
          position: relative;
          z-index: 1;
        }
        .rankAvatarBig {
          width: 82px; height: 82px;
          border-radius: 999px;
          object-fit: cover;
          border: 3px solid rgba(54,209,255,.85);
          background: rgba(255,255,255,.06);
          box-shadow: 0 16px 40px rgba(0,0,0,.35), 0 0 0 10px rgba(54,209,255,.10);
        }

        .rankTitle {
          margin-top: 10px;
          font-size: 13px;
          font-weight: 950;
          color: rgba(245,248,255,.94);
          position: relative;
          z-index: 1;
        }
        .rankBody {
          margin: 8px 0 0;
          font-size: 13px;
          line-height: 1.7;
          color: rgba(234,241,255,.78);
          position: relative;
          z-index: 1;
        }
        .cardFooter { margin-top: auto; padding-top: 12px; position: relative; z-index: 1; }

        /* SECTION 2 TABLE */
        .tableCard { padding: 14px; }
        .tableWrap {
          overflow: auto;
          border-radius: 16px;
          border: 1px solid rgba(54,209,255,.14);
          background: rgba(0,0,0,.10);
        }
        table { width: 100%; border-collapse: separate; border-spacing: 0; min-width: 860px; }
        .th {
          position: sticky; top: 0; z-index: 2;
          background: rgba(1,1,76,.92);
          border-bottom: 1px solid rgba(54,209,255,.16);
          text-align: left;
          padding: 12px;
          font-size: 12px;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: rgba(234,241,255,.72);
        }
        .dimCol { width: 320px; }
        .candCol { width: 140px; }
        .td { padding: 12px; border-bottom: 1px solid rgba(54,209,255,.10); vertical-align: top; }
        .dimLabel { font-size: 14px; color: rgba(245,248,255,.88); line-height: 1.6; }

        .thCand { display: flex; align-items: center; justify-content: flex-start; }
        .thName { font-weight: 950; letter-spacing: .01em; color: rgba(245,248,255,.92); font-size: 13px; }

        .scoreBox {
          border-radius: 14px;
          padding: 10px;
          border: 1px solid rgba(54,209,255,.12);
          background: rgba(255,255,255,.05);
        }
        .scoreTop { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 8px; }
        .scoreNum { font-size: 20px; font-weight: 950; }
        .scoreDot {
          width: 10px; height: 10px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,.26);
        }
        .scoreDot.good { background: rgba(60,245,128,0.7); }
        .scoreDot.mid  { background: rgba(238,247,72,0.72); }
        .scoreDot.warn { background: rgba(255,184,70,0.75); }

        .bar {
          height: 9px;
          border-radius: 999px;
          background: rgba(255,255,255,.07);
          border: 1px solid rgba(54,209,255,.10);
          overflow: hidden;
        }
        .barFill { height: 100%; border-radius: 999px; }
        .barFill.good { background: rgba(60,245,128,0.7); }
        .barFill.mid  { background: rgba(238,247,72,0.72); }
        .barFill.warn { background: rgba(255,184,70,0.75); }

        /* Average row */
        .avgRow { background: rgba(54,209,255,.04); }
        .avgLabel { font-weight: 900; color: rgba(245,248,255,.96); }
        .avgNum { font-size: 22px; }

        /* SECTION 3 COMPARISONS */
        .compareShell { padding: 0; }
        .compareShellInner { padding: 8px; }

        .rowAcc {
          border-radius: 18px;
          border: 1px solid rgba(54,209,255,.14);
          background: rgba(0,0,0,.08);
          margin: 10px 6px;
          overflow: hidden;
        }
        .rowAccHead {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 14px;
          background: transparent;
          border: none;
          color: inherit;
          cursor: pointer;
        }
        .rowAccLeft { display: flex; flex-direction: column; gap: 4px; }
        .rowAccTitle { font-weight: 950; letter-spacing: .01em; font-size: 15px; }
        .rowAccHint { font-size: 13px; color: rgba(234,241,255,.65); }

        .rowAccIcon {
          width: 38px; height: 38px;
          border-radius: 12px;
          display: grid; place-items: center;
          border: 1px solid rgba(54,209,255,.18);
          background: rgba(255,255,255,.05);
          font-size: 18px;
          line-height: 1;
        }

        .rowAccBody { padding: 0 14px 14px; }

        .compareRow {
          display: grid;
          grid-template-columns: 210px 1fr;
          gap: 12px;
          padding: 12px 0;
          border-top: 1px solid rgba(54,209,255,.10);
        }
        @media (max-width: 720px) { .compareRow { grid-template-columns: 1fr; } }
        .compareHeading { font-weight: 950; color: rgba(245,248,255,.92); font-size: 14px; }
        .compareText { color: rgba(234,241,255,.78); line-height: 1.75; font-size: 14px; }
      `}</style>
    </div>
  );
}
